/* src/utils/marketingTracking.js
 * gocta.ai first-party Google Ads tag (Consent Mode v2 + cross-domain linker).
 * Used on app.gocta.ai public auth pages; separate from customer form pixels
 * in tracking.js.
 *
 * Consent persistence: localStorage is origin-scoped, so gocta.ai and
 * app.gocta.ai cannot share it. We also write a first-party cookie
 * (Domain=.gocta.ai) and accept ?consent=granted|declined on the auth
 * handoff so one Accept/Decline covers both hosts. Choice mapping is
 * unchanged: Accept → granted, Decline → declined + gtag denied.
 */

const CONSENT_KEY = 'gocta-consent';
const SIGNUP_DEDUPE_KEY = 'gocta-signup-conversion-fired';
const LINKER_DOMAINS = ['gocta.ai', 'app.gocta.ai'];
const CONSENT_COOKIE_MAX_AGE = 31536000;

export function getGoogleAdsTagId() {
  return process.env.REACT_APP_GOOGLE_ADS_TAG_ID || '';
}

export function getSignupConversionLabel() {
  return process.env.REACT_APP_SIGNUP_CONVERSION_LABEL || '';
}

export function isGoogleAdsConfigured() {
  return /^AW-\d+$/.test(getGoogleAdsTagId());
}

export function isGoctaHost(hostname) {
  const host = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');
  return host === 'gocta.ai' || host.endsWith('.gocta.ai');
}

function readConsentCookie() {
  if (typeof document === 'undefined') return null;
  const prefix = `${CONSENT_KEY}=`;
  const parts = document.cookie.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trim();
    if (part.startsWith(prefix)) {
      try {
        const value = decodeURIComponent(part.slice(prefix.length));
        if (value === 'granted' || value === 'declined') return value;
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeConsentCookie(value) {
  if (typeof document === 'undefined') return;
  const parts = [
    `${CONSENT_KEY}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${CONSENT_COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  if (isGoctaHost()) {
    parts.push('Domain=.gocta.ai');
  }
  document.cookie = parts.join('; ');
}

export function persistConsentChoice(choice) {
  const value = choice === 'granted' ? 'granted' : 'declined';
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // ignore quota / private mode
  }
  writeConsentCookie(value);
  return value;
}

export function getStoredConsentChoice() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'granted' || stored === 'declined') return stored;
  } catch {
    // fall through to cookie
  }
  const fromCookie = readConsentCookie();
  if (fromCookie) {
    try {
      localStorage.setItem(CONSENT_KEY, fromCookie);
    } catch {
      // ignore
    }
    return fromCookie;
  }
  return null;
}

export function hasMarketingConsent() {
  return getStoredConsentChoice() === 'granted';
}

export function updateMarketingConsent(granted) {
  persistConsentChoice(granted ? 'granted' : 'declined');
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const state = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
}

/**
 * Honor a ?consent=granted|declined query from the marketing-site handoff,
 * persist it, then strip the param so it does not linger in the URL.
 */
export function consumeConsentHandoff() {
  if (typeof window === 'undefined') return null;
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }
  const raw = params.get('consent');
  if (raw !== 'granted' && raw !== 'declined') return null;
  updateMarketingConsent(raw === 'granted');
  params.delete('consent');
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
  window.history.replaceState({}, '', next);
  return raw;
}

/** Load gtag with Consent Mode default-denied + cross-domain linker. Idempotent. */
export function initMarketingGtag() {
  if (typeof window === 'undefined' || !isGoogleAdsConfigured()) return;
  if (window.__goctaMarketingGtagInit) return;
  window.__goctaMarketingGtagInit = true;

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });

  const tagId = getGoogleAdsTagId();
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  gtag('set', 'linker', { domains: LINKER_DOMAINS, accept_incoming: true });
  gtag('config', tagId);

  consumeConsentHandoff();
  if (hasMarketingConsent()) {
    updateMarketingConsent(true);
  }
}

/** Invoke `fn` at most once (gtag event_callback + timeout fallback share this). */
function once(fn) {
  let called = false;
  return (...args) => {
    if (called) return;
    called = true;
    if (typeof fn === 'function') fn(...args);
  };
}

/**
 * Fire primary trial-signup Lead conversion once per browser session.
 * Requires marketing consent + configured tag/label.
 *
 * `onDone` runs exactly once: gtag event_callback, 1s timeout (gtag swallows
 * callbacks when blocked), or immediately when the conversion is skipped.
 * Callers wait on this before navigate() so the conversion ping is not killed.
 */
export function fireSignupConversion(email, onDone) {
  const done = once(onDone);
  if (!isGoogleAdsConfigured() || !getSignupConversionLabel()) {
    done();
    return;
  }
  if (!hasMarketingConsent() || typeof window.gtag !== 'function') {
    done();
    return;
  }

  try {
    if (sessionStorage.getItem(SIGNUP_DEDUPE_KEY)) {
      done();
      return;
    }
    sessionStorage.setItem(SIGNUP_DEDUPE_KEY, '1');
  } catch {
    // continue — better to risk double-fire than miss conversion
  }

  const params = {
    send_to: `${getGoogleAdsTagId()}/${getSignupConversionLabel()}`,
    event_callback: done,
  };
  if (email) {
    params.user_data = { email };
  }
  window.gtag('event', 'conversion', params);
  setTimeout(done, 1000);
}
